import React, { useState, useEffect } from "react";
import {
  Plus, Trash2, GripVertical, Save, HelpCircle, CheckCircle2, Loader2, X, Award, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────
   QUIZ BUILDER - For creating/editing quizzes
   ───────────────────────────────────────── */
export function QuizBuilder({ materialId, open, onOpenChange }) {
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && materialId) {
      setLoading(true);
      axios.get(`${API}/training/materials/${materialId}/quiz?include_answers=true`)
        .then(res => setQuestions(res.data.questions || []))
        .catch(() => setQuestions([]))
        .finally(() => setLoading(false));
    }
  }, [open, materialId]);

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      id: `new-${Date.now()}`,
      question_text: "",
      question_type: "multiple_choice",
      options: ["", "", "", ""],
      correct_answer: 0,
      points: 1,
      sort_order: prev.length,
      explanation: "",
    }]);
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const updateOption = (qIdx, optIdx, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...q.options];
      opts[optIdx] = value;
      return { ...q, options: opts };
    }));
  };

  const addOption = (qIdx) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: [...q.options, ""] } : q));
  };

  const removeOption = (qIdx, optIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || q.options.length <= 2) return q;
      const opts = q.options.filter((_, j) => j !== optIdx);
      const ca = q.correct_answer >= optIdx ? Math.max(0, q.correct_answer - 1) : q.correct_answer;
      return { ...q, options: opts, correct_answer: Math.min(ca, opts.length - 1) };
    }));
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    const valid = questions.every(q => q.question_text.trim() && q.options.every(o => o.trim()) && q.options.length >= 2);
    if (!valid) { toast.error("All questions must have text and at least 2 non-empty options"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/training/materials/${materialId}/quiz`, {
        questions: questions.map((q, i) => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options,
          correct_answer: q.correct_answer,
          points: q.points,
          sort_order: i,
          explanation: q.explanation || null,
        })),
      });
      toast.success("Quiz saved");
      onOpenChange(false);
    } catch { toast.error("Failed to save quiz"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Quiz Builder
          </DialogTitle>
          <DialogDescription>Create questions for this quiz assessment</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {questions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <HelpCircle className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p>No questions yet. Add your first question below.</p>
              </div>
            ) : (
              questions.map((q, qIdx) => (
                <Card key={q.id || qIdx} data-testid={`quiz-question-${qIdx}`}>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="outline" className="shrink-0 mt-1">Q{qIdx + 1}</Badge>
                      <div className="flex-1 space-y-2">
                        <Textarea
                          value={q.question_text}
                          onChange={(e) => updateQuestion(qIdx, "question_text", e.target.value)}
                          placeholder="Enter your question..."
                          rows={2}
                          data-testid={`question-text-${qIdx}`}
                        />
                        <div className="flex gap-2">
                          <Select value={q.question_type} onValueChange={(v) => {
                            if (v === "true_false") {
                              updateQuestion(qIdx, "question_type", v);
                              updateQuestion(qIdx, "options", ["True", "False"]);
                              updateQuestion(qIdx, "correct_answer", 0);
                            } else {
                              updateQuestion(qIdx, "question_type", v);
                            }
                          }}>
                            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                              <SelectItem value="true_false">True/False</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            value={q.points}
                            onChange={(e) => updateQuestion(qIdx, "points", parseInt(e.target.value) || 1)}
                            className="w-20"
                            title="Points"
                          />
                          <span className="text-xs text-muted-foreground self-center">pts</span>
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => removeQuestion(qIdx)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Options */}
                    <div className="space-y-2 ml-8">
                      <Label className="text-xs text-muted-foreground">Options (click radio to set correct answer)</Label>
                      {q.options.map((opt, optIdx) => (
                        <div key={optIdx} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQuestion(qIdx, "correct_answer", optIdx)}
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                              q.correct_answer === optIdx
                                ? "border-emerald-500 bg-emerald-500 text-white"
                                : "border-muted-foreground/30 hover:border-primary"
                            }`}
                          >
                            {q.correct_answer === optIdx && <CheckCircle2 className="h-3 w-3" />}
                          </button>
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(qIdx, optIdx, e.target.value)}
                            placeholder={`Option ${optIdx + 1}`}
                            className="flex-1"
                            data-testid={`option-${qIdx}-${optIdx}`}
                          />
                          {q.question_type !== "true_false" && q.options.length > 2 && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeOption(qIdx, optIdx)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {q.question_type !== "true_false" && q.options.length < 6 && (
                        <Button variant="ghost" size="sm" onClick={() => addOption(qIdx)}>
                          <Plus className="h-3 w-3 mr-1" /> Add Option
                        </Button>
                      )}
                    </div>

                    {/* Explanation */}
                    <div className="ml-8">
                      <Input
                        value={q.explanation || ""}
                        onChange={(e) => updateQuestion(qIdx, "explanation", e.target.value)}
                        placeholder="Explanation (shown after answering, optional)"
                        className="text-sm"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            <Button variant="outline" className="w-full" onClick={addQuestion} data-testid="add-question-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || questions.length === 0} data-testid="save-quiz-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Quiz ({questions.length} questions)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────────────────────
   QUIZ TAKER - For taking quizzes
   ───────────────────────────────────────── */
export function QuizTaker({ assignmentId, materialId, materialTitle, open, onOpenChange, onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && materialId) {
      setLoading(true);
      setResults(null);
      setAnswers({});
      axios.get(`${API}/training/materials/${materialId}/quiz`)
        .then(res => setQuestions(res.data.questions || []))
        .catch(() => toast.error("Failed to load quiz"))
        .finally(() => setLoading(false));
    }
  }, [open, materialId]);

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Please answer all questions"); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/training/assignments/${assignmentId}/quiz/${materialId}/submit`, { answers });
      setResults(res.data);
      if (res.data.passed && onComplete) onComplete();
    } catch { toast.error("Failed to submit quiz"); }
    finally { setSubmitting(false); }
  };

  const handleRetry = () => {
    setResults(null);
    setAnswers({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {materialTitle || "Quiz"}
          </DialogTitle>
          <DialogDescription>
            {results ? `Score: ${results.score}%` : `${questions.length} questions`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : results ? (
          /* ─── Results View ─── */
          <div className="space-y-4">
            <div className={`text-center p-6 rounded-lg ${results.passed ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
              <div className={`text-5xl font-bold ${results.passed ? "text-emerald-600" : "text-red-600"}`}>
                {results.score}%
              </div>
              <p className={`text-lg font-medium mt-1 ${results.passed ? "text-emerald-600" : "text-red-600"}`}>
                {results.passed ? "Passed!" : "Not Passed"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {results.earned_points}/{results.total_points} points
                {results.passing_score && ` (${results.passing_score}% required)`}
                {` | Attempt ${results.attempts}`}
              </p>
            </div>
            <Separator />
            <div className="space-y-3">
              {results.results.map((r, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${r.is_correct ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 ${r.is_correct ? "text-emerald-600" : "text-red-600"}`}>
                      {r.is_correct ? <CheckCircle2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{r.question_text}</p>
                      <div className="mt-1.5 space-y-1">
                        {r.options.map((opt, oi) => (
                          <div key={oi} className={`text-sm px-2 py-0.5 rounded ${
                            oi === r.correct_answer ? "bg-emerald-500/10 text-emerald-700 font-medium" :
                            oi === r.selected_answer && !r.is_correct ? "bg-red-500/10 text-red-700 line-through" :
                            "text-muted-foreground"
                          }`}>
                            {oi === r.correct_answer ? "✓ " : oi === r.selected_answer ? "✗ " : "  "}{opt}
                          </div>
                        ))}
                      </div>
                      {r.explanation && (
                        <p className="text-xs text-muted-foreground mt-2 italic">{r.explanation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ─── Questions View ─── */
          <div className="space-y-6">
            {questions.map((q, qIdx) => (
              <div key={q.id} className="space-y-2" data-testid={`take-question-${qIdx}`}>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="shrink-0 mt-0.5">{qIdx + 1}</Badge>
                  <p className="font-medium">{q.question_text}</p>
                  <Badge variant="secondary" className="shrink-0 text-xs">{q.points}pt{q.points > 1 ? "s" : ""}</Badge>
                </div>
                <div className="ml-8 space-y-1.5">
                  {q.options.map((opt, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => setAnswers(prev => ({ ...prev, [q.id]: optIdx }))}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-md border transition-all ${
                        answers[q.id] === optIdx
                          ? "border-primary bg-primary/10 font-medium"
                          : "border-border hover:bg-accent"
                      }`}
                      data-testid={`answer-${qIdx}-${optIdx}`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${
                        answers[q.id] === optIdx ? "border-primary bg-primary" : "border-muted-foreground/30"
                      }`}>
                        {answers[q.id] === optIdx && <div className="w-full h-full rounded-full bg-primary" />}
                      </div>
                      <span className="text-sm">{opt}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {results ? (
            <div className="flex gap-2 w-full justify-between">
              {!results.passed && (
                <Button variant="outline" onClick={handleRetry} data-testid="retry-quiz-btn">
                  Try Again
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)}>
                {results.passed ? "Done" : "Close"}
              </Button>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || Object.keys(answers).length < questions.length}
                data-testid="submit-quiz-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit ({Object.keys(answers).length}/{questions.length})
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────────────────────
   CERTIFICATE VIEWER
   ───────────────────────────────────────── */
export function CertificateViewer({ certificate, open, onOpenChange }) {
  const [downloading, setDownloading] = useState(false);
  if (!certificate) return null;

  const handleDownload = async () => {
    if (!certificate.assignment_id) {
      toast.error("Missing assignment reference");
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/training/assignments/${certificate.assignment_id}/certificate/pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${certificate.certificate_number || "training"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Failed to download certificate");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="sr-only">
          <DialogTitle>Certificate of Completion - {certificate.program_title}</DialogTitle>
          <DialogDescription>
            Certificate issued to {certificate.recipient_name} for completing {certificate.program_title}.
          </DialogDescription>
        </DialogHeader>
        <div className="border-4 border-double border-primary/30 rounded-lg p-8 text-center space-y-4 bg-gradient-to-b from-primary/5 to-transparent" data-testid="certificate-view">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Award className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Certificate of Completion</p>
            <h2 className="text-2xl font-bold mt-2">{certificate.program_title}</h2>
          </div>
          <Separator />
          <div>
            <p className="text-sm text-muted-foreground">This certifies that</p>
            <p className="text-xl font-semibold mt-1">{certificate.recipient_name}</p>
            <p className="text-sm text-muted-foreground mt-2">
              has successfully completed the training program
              {certificate.score != null && ` with a score of ${certificate.score}%`}
            </p>
          </div>
          <div className="pt-4 flex justify-between text-xs text-muted-foreground">
            <span>Cert #{certificate.certificate_number}</span>
            <span>{certificate.issued_at ? new Date(certificate.issued_at).toLocaleDateString() : ""}</span>
          </div>
        </div>
        <DialogFooter className="justify-center sm:justify-center gap-2">
          <Button variant="outline" onClick={handleDownload} disabled={downloading} data-testid="download-certificate-pdf-btn">
            {downloading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download PDF
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="close-certificate-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
